import googleCalendarIcon from "@/assets/google_calendar.svg"
import activecampaignIcon from "@/assets/logos/activecampaign-icon.svg"
import asanaIcon from "@/assets/logos/asana-icon.svg"
import brevoIcon from "@/assets/logos/brevo-icon.svg"
import calComIcon from "@/assets/logos/cal-com-icon.svg"
import calendlyIcon from "@/assets/logos/calendly-icon.svg"
import copperIcon from "@/assets/logos/copper-icon.svg"
import freshsalesIcon from "@/assets/logos/freshsales-icon.svg"
import googleBusinessProfileIcon from "@/assets/logos/google-business-profile-icon.svg"
import hubspotIcon from "@/assets/logos/hubspot-icon.svg"
import intercomIcon from "@/assets/logos/intercom-icon.svg"
import keapIcon from "@/assets/logos/keap-icon.svg"
import klaviyoIcon from "@/assets/logos/klaviyo-icon.svg"
import mailchimpIcon from "@/assets/logos/mailchimp-icon.svg"
import pipedriveIcon from "@/assets/logos/pipedrive-icon.svg"
import quickbooksIcon from "@/assets/logos/quickbooks-icon.svg"
import stripeIcon from "@/assets/logos/stripe-icon.svg"
import outlookCalendarIcon from "@/assets/outlook_calendar.svg"

const CDN = "https://cdn.jsdelivr.net/gh/gilbarbara/logos/logos"

function CdnLogo({ name, label }: { name: string; label: string }) {
  return (
    <img
      src={`${CDN}/${name}.svg`}
      alt={label}
      className="h-8 w-8 object-contain"
    />
  )
}

function LocalLogo({ src, label }: { src: string; label: string }) {
  return <img src={src} alt={label} className="h-8 w-8 object-contain" />
}

export interface IntegrationResourceType {
  value: string
  label: string
  service: string
  action: string
  countLabel: string
  searchable: boolean
}

// A credential field for an API-key integration's Connect/Manage dialog. `key`
// is the param posted to the backend (must match Integrations::ApiKeyRegistry).
export interface CredentialField {
  key: string
  label: string
  secret?: boolean
  placeholder?: string
  help?: string
}

export const INTEGRATION_REGISTRY: Record<
  string,
  {
    label: string
    description: string
    icon: React.ReactNode
    resourceTypes: IntegrationResourceType[]
    // Present for API-key integrations — drives the generic Connect/Manage form.
    credentials?: CredentialField[]
  }
> = {
  google_workspace: {
    label: "Google Workspace",
    description:
      "Read and write files in Drive, send Gmail, and manage Calendar.",
    resourceTypes: [
      {
        value: "google_drive_file",
        label: "Google Drive File",
        service: "Google Drive",
        action: "Read files",
        countLabel: "file",
        searchable: true,
      },
      {
        value: "google_drive_folder",
        label: "Google Drive Folder",
        service: "Google Drive",
        action: "Create files",
        countLabel: "folder",
        searchable: true,
      },
    ],
    icon: (
      <svg viewBox="0 0 24 24" className="h-8 w-8" fill="none">
        <path
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
          fill="#4285F4"
        />
        <path
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          fill="#34A853"
        />
        <path
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          fill="#FBBC05"
        />
        <path
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          fill="#EA4335"
        />
      </svg>
    ),
  },
  notion: {
    label: "Notion",
    description: "Read and update pages and databases in your workspace.",
    resourceTypes: [
      {
        value: "notion_page",
        label: "Notion Page",
        service: "Projects",
        action: "Read pages",
        countLabel: "page",
        searchable: true,
      },
    ],
    icon: (
      <svg viewBox="0 0 24 24" className="h-8 w-8" fill="currentColor">
        <path d="M4.459 4.208c.746.606 1.026.56 2.428.466l13.215-.793c.28 0 .047-.28-.046-.326L18.03 2.31c-.466-.373-.84-.56-1.772-.466L3.293 3.09c-.467.046-.56.28-.374.466l1.54 1.652zm.793 3.358v13.854c0 .746.373 1.026 1.213.98l14.523-.84c.84-.046.933-.56.933-1.166V6.726c0-.606-.233-.933-.746-.886l-15.177.886c-.56.047-.746.327-.746.84zm14.336.42c.093.42 0 .84-.42.886l-.7.14v10.264c-.607.327-1.166.513-1.633.513-.746 0-.933-.233-1.493-.933l-4.572-7.18v6.953l1.446.327s0 .84-1.166.84l-3.22.186c-.093-.186 0-.653.327-.746l.84-.233V9.854L7.822 9.76c-.094-.42.14-1.026.793-1.073l3.453-.233 4.759 7.274V9.387l-1.213-.14c-.093-.513.28-.886.746-.933l3.22-.186z" />
      </svg>
    ),
  },
  salesforce: {
    label: "Salesforce",
    description:
      "Sync leads, contacts, opportunities, and pipeline activity with your CRM.",
    resourceTypes: [],
    icon: <CdnLogo name="salesforce" label="Salesforce" />,
  },
  hubspot: {
    label: "HubSpot",
    description:
      "Connect your CRM, email marketing, deals, and sales pipeline.",
    resourceTypes: [],
    icon: <LocalLogo src={hubspotIcon} label="HubSpot" />,
  },
  hubspot_crm: {
    label: "HubSpot",
    description:
      "Connect HubSpot so the Lead Responder can watch a list and work new contacts.",
    resourceTypes: [],
    icon: <LocalLogo src={hubspotIcon} label="HubSpot" />,
    credentials: [
      {
        key: "api_key",
        label: "Private App token",
        secret: true,
        help: "Create a Private App with CRM read scopes in HubSpot → Settings → Integrations → Private Apps.",
      },
    ],
  },
  pipedrive: {
    label: "Pipedrive",
    description:
      "Connect your Pipedrive CRM to sync contacts, leads, and deals.",
    resourceTypes: [],
    icon: <LocalLogo src={pipedriveIcon} label="Pipedrive" />,
    credentials: [
      {
        key: "api_token",
        label: "API token",
        secret: true,
        help: "Find it in Pipedrive → Settings → Personal preferences → API.",
      },
    ],
  },
  close: {
    label: "Close",
    description:
      "Connect your Close CRM to sync leads and contacts from your agents.",
    resourceTypes: [],
    icon: <CdnLogo name="close" label="Close" />,
    credentials: [
      {
        key: "api_key",
        label: "API key",
        secret: true,
        help: "Find it in Close → Settings → API Keys.",
      },
    ],
  },
  copper: {
    label: "Copper",
    description:
      "Connect your Copper CRM to sync people and leads from your agents.",
    resourceTypes: [],
    icon: <LocalLogo src={copperIcon} label="Copper" />,
    credentials: [
      {
        key: "api_key",
        label: "API key",
        secret: true,
        help: "Generate it in Copper → Settings → Integrations → API Keys.",
      },
      {
        key: "email",
        label: "Account email",
        placeholder: "you@company.com",
        help: "The email of the user the API key belongs to.",
      },
    ],
  },
  keap: {
    label: "Keap",
    description:
      "Connect your Keap CRM to sync contacts and leads from your agents.",
    resourceTypes: [],
    icon: <LocalLogo src={keapIcon} label="Keap" />,
    credentials: [
      {
        key: "api_key",
        label: "Service Account Key",
        secret: true,
        help: "Create one in Keap → Settings → API.",
      },
    ],
  },
  freshsales: {
    label: "Freshsales",
    description:
      "Connect your Freshsales CRM to sync contacts and leads from your agents.",
    resourceTypes: [],
    icon: <LocalLogo src={freshsalesIcon} label="Freshsales" />,
    credentials: [
      {
        key: "domain",
        label: "Domain",
        placeholder: "acme",
        help: "Your Freshsales subdomain — the acme in acme.myfreshworks.com.",
      },
      {
        key: "api_key",
        label: "API key",
        secret: true,
        help: "Find it in Freshsales → Profile Settings → API Settings.",
      },
    ],
  },
  mailchimp: {
    label: "Mailchimp",
    description:
      "Connect Mailchimp to add captured contacts to your audiences.",
    resourceTypes: [],
    icon: <LocalLogo src={mailchimpIcon} label="Mailchimp" />,
    credentials: [
      {
        key: "api_key",
        label: "API key",
        secret: true,
        help: "Find it in Mailchimp → Account → Extras → API keys.",
      },
    ],
  },
  klaviyo: {
    label: "Klaviyo",
    description: "Connect Klaviyo to add captured contacts to your lists.",
    resourceTypes: [],
    icon: <LocalLogo src={klaviyoIcon} label="Klaviyo" />,
    credentials: [
      {
        key: "api_key",
        label: "Private API key",
        secret: true,
        help: "Create a private key in Klaviyo → Settings → API keys.",
      },
    ],
  },
  active_campaign: {
    label: "ActiveCampaign",
    description:
      "Connect ActiveCampaign to add captured contacts to your lists.",
    resourceTypes: [],
    icon: <LocalLogo src={activecampaignIcon} label="ActiveCampaign" />,
    credentials: [
      {
        key: "api_url",
        label: "API URL",
        placeholder: "https://your-account.api-us1.com",
        help: "Find it in ActiveCampaign → Settings → Developer.",
      },
      {
        key: "api_key",
        label: "API key",
        secret: true,
        help: "Find it in ActiveCampaign → Settings → Developer.",
      },
    ],
  },
  brevo: {
    label: "Brevo",
    description: "Connect Brevo to add captured contacts to your email lists.",
    resourceTypes: [],
    icon: <LocalLogo src={brevoIcon} label="Brevo" />,
    credentials: [
      {
        key: "api_key",
        label: "API key",
        secret: true,
        help: "Create one in Brevo → SMTP & API → API Keys.",
      },
    ],
  },
  quickbooks: {
    label: "QuickBooks",
    description:
      "Track invoices, chase overdue payments, and reconcile accounts.",
    resourceTypes: [],
    icon: <LocalLogo src={quickbooksIcon} label="QuickBooks" />,
  },
  xero: {
    label: "Xero",
    description: "Keep invoices, bills, and accounting records up to date.",
    resourceTypes: [],
    icon: <CdnLogo name="xero" label="Xero" />,
  },
  slack: {
    label: "Slack",
    description:
      "Post messages, summaries, and alerts to channels and direct messages.",
    resourceTypes: [],
    icon: (
      <svg viewBox="0 0 24 24" className="h-8 w-8" fill="none">
        <path
          d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.52-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z"
          fill="#E01E5A"
        />
        <path
          d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312z"
          fill="#36C5F0"
        />
        <path
          d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312z"
          fill="#2EB67D"
        />
        <path
          d="M15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"
          fill="#ECB22E"
        />
      </svg>
    ),
  },
  outlook: {
    label: "Outlook",
    description: "Read and send email through Microsoft 365 accounts.",
    resourceTypes: [],
    icon: <CdnLogo name="microsoft-icon" label="Outlook" />,
  },
  zendesk: {
    label: "Zendesk",
    description: "Create tickets, update customers, and track support issues.",
    resourceTypes: [],
    icon: <CdnLogo name="zendesk-icon" label="Zendesk" />,
  },
  twilio: {
    label: "Twilio",
    description: "Make phone calls and send SMS messages through your agents.",
    resourceTypes: [],
    icon: <CdnLogo name="twilio-icon" label="Twilio" />,
  },
  stripe: {
    label: "Stripe",
    description:
      "Track payments, invoices, and subscription status in real time.",
    resourceTypes: [],
    icon: <LocalLogo src={stripeIcon} label="Stripe" />,
  },
  intercom: {
    label: "Intercom",
    description:
      "Read conversations, create tickets, and send messages to customers and leads.",
    resourceTypes: [],
    icon: <LocalLogo src={intercomIcon} label="Intercom" />,
  },
  asana: {
    label: "Asana",
    description:
      "Create tasks, update projects, and track work across your team's workspace.",
    resourceTypes: [],
    icon: <LocalLogo src={asanaIcon} label="Asana" />,
  },
  filevine: {
    label: "Filevine",
    description:
      "Access matters, contacts, and documents in your Filevine workspace.",
    resourceTypes: [],
    icon: (
      <svg viewBox="0 0 137 206" className="h-8 w-8" fill="currentColor">
        <path d="M0.5 128.906V90.125C0.5 82.5789 4.54708 75.6163 11.1057 71.8562L136.5 0V39.3381C136.5 46.9101 132.427 53.8987 125.829 57.6458L0.5 128.906Z" />
        <path d="M0.5 206.001V155.032L91.3315 103.312V142.715C91.3315 150.326 87.2194 157.34 80.5566 161.074L0.5 206.001Z" />
      </svg>
    ),
  },
  google_calendar: {
    label: "Google Calendar",
    description:
      "Book appointments and check real-time availability via Google Calendar.",
    resourceTypes: [],
    icon: (
      <img src={googleCalendarIcon} alt="Google Calendar" className="h-8 w-8" />
    ),
  },
  outlook_calendar: {
    label: "Outlook Calendar",
    description:
      "Book appointments and check real-time availability via Outlook.",
    resourceTypes: [],
    icon: (
      <img
        src={outlookCalendarIcon}
        alt="Outlook Calendar"
        className="h-8 w-8"
      />
    ),
  },
  cal_com: {
    label: "Cal.com",
    description:
      "Open-source scheduling for bookings, routing forms, and availability.",
    resourceTypes: [],
    icon: <LocalLogo src={calComIcon} label="Cal.com" />,
  },
  calendly: {
    label: "Calendly",
    description:
      "Book appointments and check real-time availability on behalf of clients.",
    resourceTypes: [],
    icon: <LocalLogo src={calendlyIcon} label="Calendly" />,
  },
  google_business_profile: {
    label: "Google Business Profile",
    description: "Read and reply to reviews on your Google Business Profile.",
    resourceTypes: [],
    icon: (
      <LocalLogo
        src={googleBusinessProfileIcon}
        label="Google Business Profile"
      />
    ),
  },
}
